import type { INotificationToast, ShowToastParams } from './index.h'

import { createEvent, createStore, sample } from 'effector'
import { register } from '~/core/repository'
import { noop } from '~/shared/effector'
import { NOTIFICATION_TOAST_DURATION, NotificationToastType } from './index.h'

export const showToast = createEvent<ShowToastParams>()
export const hideToast = createEvent<string>()
export const hideAllToasts = createEvent()

// Internal event with generated ID
interface ToastWithId extends ShowToastParams {
  id: string
}

const toastCreated = createEvent<ToastWithId>()

// Generate unique ID for each toast
sample({
  clock: showToast,
  fn: (params): ToastWithId => ({
    ...params,
    id: `toast-${Date.now()}-${Math.random()}`,
  }),
  target: toastCreated,
})

// Store for toast timers
const $toastTimers = createStore<Map<string, ReturnType<typeof setTimeout>>>(
  new Map()
)
  .on(toastCreated, (timers, toast) => {
    const duration = toast.duration || NOTIFICATION_TOAST_DURATION

    if (duration > 0) {
      const timer = setTimeout(() => {
        hideToast(toast.id)
      }, duration)

      const newTimers = new Map(timers)
      newTimers.set(toast.id, timer)
      return newTimers
    }

    return timers
  })
  .on(hideToast, (timers, id) => {
    const timer = timers.get(id)
    if (timer) {
      clearTimeout(timer)
      const newTimers = new Map(timers)
      newTimers.delete(id)
      return newTimers
    }
    return timers
  })
  .reset(hideAllToasts)

export const $toasts = createStore<INotificationToast[]>([])
  .on(toastCreated, (toasts, toast) => {
    const newToast: INotificationToast = {
      id: toast.id,
      message: toast.message,
      type: toast.type || NotificationToastType.INFO,
      duration: toast.duration || NOTIFICATION_TOAST_DURATION,
      icon: toast.icon,
    }
    return [...toasts, newToast]
  })
  .on(hideToast, (toasts, id) => {
    return toasts.filter((toast) => toast.id !== id)
  })
  .reset(hideAllToasts)

// Clear all timers when hiding all toasts
sample({
  clock: hideAllToasts,
  source: $toastTimers,
  fn: (timers) => {
    timers.forEach((timer) => clearTimeout(timer))
  },
  target: noop,
})

register($toasts, '$entities/notification-toasts/toasts')
register(showToast, 'entities/notification-toasts/showToast')
register(hideToast, 'entities/notification-toasts/hideToast')
register(hideAllToasts, 'entities/notification-toasts/hideAllToasts')
